import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaFlowAuth from '../auth/MiaFlowAuth';
import config from '../config.js';
import CreateProjectModal from '../components/CreateProjectModal';
import CreateFolderModal from '../components/CreateFolderModal';
import ProjectCard from '../components/ProjectCard';
import Toast from '../components/Toast';
import {getProjectThumbnail} from '../utils/screenshotCapture';
import {getBffHeaders} from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../context/AuthContext';
import { clearAllPreviewSessions } from '../components/Canvas';
import './Projects.css';

const Projects = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { filter } = useParams();
  const [projects, setProjects] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(() => miaFlowAuth.getSelectedTenant());
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [initialProjectPrompt, setInitialProjectPrompt] = useState('');
  const [toast, setToast] = useState(null);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isProjectsMenuOpen, setIsProjectsMenuOpen] = useState(false);
  const [projectContextMenu, setProjectContextMenu] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('projectsViewMode') || 'grid');
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [showFavorites, setShowFavorites] = useState(() => localStorage.getItem('showFavorites') === 'true');
  
  // Toast helpers
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };
  
  const hideToast = () => {
    setToast(null);
  };
  
  // Initialize analytics
  const { trackProject, trackAction, trackSearch, trackFeature } = useAnalytics();
  
  // Helper to format dates safely
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };
  
  // Helper to get owner/creator name
  const getOwnerName = (project) => {
    if (project.creatorName) return project.creatorName;
    if (user?.email && project.configurationGitPath?.includes(user.email)) return 'Me';
    return '—';
  };
  
  // Helper to format project meta (for subtitle)
  const formatProjectMeta = (project) => {
    const owner = getOwnerName(project);
    const date = formatDate(project.visitedAt || project.updatedAt || project.createdAt);
    if (date === '—') {
      return `Created by ${owner}`;
    }
    return `Modified by ${owner}, ${date}`;
  };
  
  // Fetch favorites
  const fetchFavorites = useCallback(async () => {
    if (!selectedTenant?.tenantId || !showFavorites) return;
    
    try {
      const favoritesData = await miaPlatformDataService.getFavorites(selectedTenant.tenantId);
      // Convert array of project IDs to Set
      setFavorites(new Set(favoritesData || []));
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setFavorites(new Set());
    }
  }, [selectedTenant?.tenantId, showFavorites]);

  // Fetch folders
  const fetchFolders = useCallback(async () => {
    if (!selectedTenant?.tenantId) return;
    
    try {
      const foldersData = await miaPlatformDataService.getFolders(selectedTenant.tenantId);
      setFolders(foldersData || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  }, [selectedTenant?.tenantId]);

  // Fetch favorites on mount and tenant change
  useEffect(() => {
    // Wait for authentication to be ready before fetching
    if (isAuthLoading) return;
    
    fetchFavorites();
  }, [fetchFavorites, isAuthLoading]);

  // Listen for tenant changes
  useEffect(() => {
    const handleTenantChange = (event) => {
      setSelectedTenant(event.detail.tenant);
    };
    
    window.addEventListener('mia-flow-tenant-changed', handleTenantChange);
    
    return () => {
      window.removeEventListener('mia-flow-tenant-changed', handleTenantChange);
    };
  }, []);

  // Listen for feature toggle changes
  useEffect(() => {
    const handleFeatureToggle = (event) => {
      if (event.detail.showFavorites !== undefined) {
        setShowFavorites(event.detail.showFavorites);
      }
    };
    
    window.addEventListener('featureToggleChanged', handleFeatureToggle);
    
    return () => {
      window.removeEventListener('featureToggleChanged', handleFeatureToggle);
    };
  }, []);

  // Fetch folders and favorites when tenant changes
  useEffect(() => {
    // Wait for authentication to be ready before fetching
    if (isAuthLoading) return;
    
    fetchFolders();
    fetchFavorites();
  }, [fetchFolders, fetchFavorites, isAuthLoading]);
  
  // Filter states
  const [sortBy, setSortBy] = useState('last-visited');
  const [visibilityFilter, setVisibilityFilter] = useState('any');
  const [statusFilter, setStatusFilter] = useState('any');
  const [creatorFilter, setCreatorFilter] = useState('all');

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await miaPlatformDataService.getProjects(
        selectedTenant?.tenantId,
        searchQuery || null,
        1,
        50
      );
      
      const projectsData = result.data || [];
      
      // Fetch folder assignments
      const folderMap = await miaPlatformDataService.getProjectFolders(
        selectedTenant?.tenantId,
        projectsData.map(p => p._id)
      );
      
      // Merge folder IDs into projects
      const projectsWithFolders = projectsData.map(project => ({
        ...project,
        folderId: folderMap[project._id] || null
      }));
      
      // Display projects immediately without waiting for thumbnails
      setProjects(projectsWithFolders);
      setLoading(false);
      
      // Fetch thumbnails asynchronously in the background
      // Return abort controller to allow cleanup
      const thumbnailFetches = projectsData.map(async (project) => {
        try {
          const thumbnail = await getProjectThumbnail(project._id, selectedTenant?.tenantId);
          if (thumbnail) {
            // Update only this specific project's thumbnail
            setProjects(prevProjects => 
              prevProjects.map(p => 
                p._id === project._id ? { ...p, thumbnail } : p
              )
            );
          }
        } catch (error) {
          // Silently ignore thumbnail fetch errors
        }
      });
      
      return thumbnailFetches;
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.message || 'Failed to load projects');
      setLoading(false);
      return [];
    }
  }, [selectedTenant, searchQuery]);

  // Debounced search effect
  useEffect(() => {
    // Wait for authentication to be ready before fetching
    if (isAuthLoading) {
      return;
    }
    
    let cancelled = false;
    
    // Debounce search: wait 300ms after user stops typing
    const timeoutId = setTimeout(() => {
      const loadProjects = async () => {
        const fetches = await fetchProjects();
        // Wait for thumbnails only if not cancelled
        if (!cancelled && fetches) {
          await Promise.allSettled(fetches);
        }
      };
      
      loadProjects();
    }, searchQuery ? 300 : 0); // No delay on initial load or when clearing search
    
    // Listen for thumbnail updates from Canvas
    const handleThumbnailUpdate = (event) => {
      const { projectId, thumbnailUrl } = event.detail;
      setProjects(prevProjects => 
        prevProjects.map(p => 
          p._id === projectId ? { ...p, thumbnail: thumbnailUrl } : p
        )
      );
    };
    
    window.addEventListener('thumbnail-updated', handleThumbnailUpdate);
    
    // Cleanup: cancel pending requests and clear timeout
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      window.removeEventListener('thumbnail-updated', handleThumbnailUpdate);
    };
  }, [fetchProjects, searchQuery, isAuthLoading]);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 2) {
      trackSearch(query, { 
        context: 'projects',
        resultCount: projects.filter(p => 
          p.name?.toLowerCase().includes(query.toLowerCase())
        ).length 
      });
      trackFeature('project_search');
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    trackAction('project', 'refresh', {});
    await Promise.all([fetchFolders(), fetchProjects()]);
  };

  // Handle create project click
  const handleCreateProjectClick = (prompt = '') => {
    if (!selectedTenant) {
      alert('Please select an organization first');
      return;
    }
    // Ensure prompt is always a string
    const promptStr = typeof prompt === 'string' ? prompt : '';
    setInitialProjectPrompt(promptStr);
    setIsCreateProjectModalOpen(true);
  };

  // Expose handleCreateProjectClick globally for other components
  useEffect(() => {
    window.miaFlowCreateProject = handleCreateProjectClick;
    return () => {
      delete window.miaFlowCreateProject;
    };
  }, [selectedTenant]);

  // Handle folder creation
  const handleCreateFolder = async (folderData) => {
    try {
      if (!selectedTenant) {
        throw new Error('Please select an organization first');
      }

      const folder = await miaPlatformDataService.createFolder(
        selectedTenant.tenantId,
        folderData
      );

      trackAction('project', 'folder_create', { folderName: folderData.name || folder.name });

      // Refresh folders list
      await fetchFolders();
      
      showToast(`Folder "${folder.name}" created successfully!`, 'success');
      setIsCreateFolderModalOpen(false);
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error; // Re-throw to let modal handle the error display
    }
  };

  // Handle moving project to folder
  const handleMoveToFolder = async (projectId, folderId) => {
    try {
      await miaPlatformDataService.updateProject(
        selectedTenant.tenantId,
        projectId,
        { folderId }
      );
      
      // Update local state
      setProjects(projects.map(p => 
        p._id === projectId ? { ...p, folderId } : p
      ));
      
      const folderName = folderId 
        ? folders.find(f => f.folderId === folderId)?.name 
        : 'root';
      
      showToast(`Project moved to ${folderName}`, 'success');
      setProjectContextMenu(null);
    } catch (error) {
      console.error('Error moving project to folder:', error);
      showToast('Failed to move project', 'error');
    }
  };

  // Handle rename folder
  const handleStartRenameFolder = (folder, event) => {
    event.stopPropagation();
    setRenamingFolder(folder.folderId);
    setRenameFolderValue(folder.name);
  };

  const handleRenameFolder = async (folderId) => {
    if (!renameFolderValue.trim()) {
      showToast('Folder name cannot be empty', 'error');
      return;
    }

    const oldName = renamingFolder ? folders.find(f => f.folderId === folderId)?.name : '';

    try {
      await miaPlatformDataService.updateFolder(
        selectedTenant.tenantId,
        folderId,
        { name: renameFolderValue.trim() }
      );
      
      trackAction('project', 'folder_rename', { 
        folderId, 
        oldName,
        newName: renameFolderValue.trim() 
      });
      
      await fetchFolders();
      setRenamingFolder(null);
      setRenameFolderValue('');
      showToast('Folder renamed successfully', 'success');
    } catch (error) {
      console.error('Error renaming folder:', error);
      showToast('Failed to rename folder', 'error');
    }
  };

  const handleCancelRenameFolder = () => {
    setRenamingFolder(null);
    setRenameFolderValue('');
  };

  // Handle delete folder
  const handleDeleteFolder = async (folderId) => {
    const folder = folders.find(f => f.folderId === folderId);
    
    try {
      await miaPlatformDataService.deleteFolder(selectedTenant.tenantId, folderId);
      
      trackAction('project', 'folder_delete', { 
        folderId,
        projectCount: folder?.projects?.length || 0
      });
      
      // Refresh both folders and projects to show any orphaned projects
      await Promise.all([fetchFolders(), fetchProjects()]);
      
      setDeletingFolder(null);
      showToast('Folder deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting folder:', error);
      if (error.message?.includes('FOLDER_NOT_EMPTY') || error.response?.data?.message?.includes('not empty')) {
        showToast('Cannot delete folder: it contains subfolders', 'error');
      } else {
        showToast('Failed to delete folder', 'error');
      }
    }
  };

  const handleConfirmDeleteFolder = (folderId, event) => {
    event.stopPropagation();
    setDeletingFolder(folderId);
  };

  const handleCancelDeleteFolder = () => {
    setDeletingFolder(null);
  };

  // Toggle projects menu
  const toggleProjectsMenu = () => {
    setIsProjectsMenuOpen(!isProjectsMenuOpen);
  };

  // Toggle view mode
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('projectsViewMode', mode);
    trackAction('project', 'view_mode_changed', { mode });
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (projectId, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    // Optimistic update
    const newFavorites = new Set(favorites);
    const wasFavorited = newFavorites.has(projectId);
    if (wasFavorited) {
      newFavorites.delete(projectId);
    } else {
      newFavorites.add(projectId);
    }
    setFavorites(newFavorites);
    
    trackProject('favorite_toggle', { projectId, isFavorite: !wasFavorited });
    
    try {
      await miaPlatformDataService.toggleFavorite(projectId);
      showToast(
        wasFavorited ? 'Removed from favorites' : 'Added to favorites',
        'success'
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert on error
      setFavorites(favorites);
      showToast('Failed to update favorite status', 'error');
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProjectsMenuOpen && !event.target.closest('.projects-menu-container')) {
        setIsProjectsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isProjectsMenuOpen]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (projectContextMenu) {
        setProjectContextMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [projectContextMenu]);

  // Handle create project submission
  const handleCreateProject = async (projectData) => {
    const startTime = Date.now();
    
    trackProject('create_started', {
      template: projectData.templateId,
      hasPrompt: !!initialProjectPrompt,
      tenantId: projectData.tenantId
    });
    
    try {
      // Call the service to create project via BFF
      const result = await miaPlatformDataService.createProject(projectData);
      
      trackProject('create_completed', {
        projectId: result.project?._id,
        projectName: projectData.projectName,
        template: projectData.templateId,
        tenantId: projectData.tenantId,
        duration: Date.now() - startTime
      });
      
      // Track feature usage
      trackFeature('project_creation', {
        template: projectData.templateId,
        duration: Date.now() - startTime
      });
      
      // Refresh projects list
      await fetchProjects();
      
      // Show success toast
      showToast(`🎉 Project "${projectData.projectName}" created successfully!`, 'success');
      
      // Navigate to chat with the new project
      const tenantId = projectData.tenantId;
      const projectId = result.project?._id;
      
      if (tenantId && projectId) {
        // Small delay to let user see the toast
        setTimeout(() => {
          // Set flag to prevent URL reconstruction (new project = no service selected)
          sessionStorage.setItem('mia_switching_to_project', projectId);
          navigate(`/chat/${tenantId}/${projectId}`);
        }, 1000);
      } else {
        // Fallback to chat root if we don't have the IDs
        setTimeout(() => {
          navigate('/chat');
        }, 1000);
      }
    } catch (error) {
      console.error('Error creating project:', error);
      trackProject('create_failed', { 
        error: error.message,
        template: projectData.templateId,
        duration: Date.now() - startTime
      });
      throw error; // Re-throw to let modal handle it
    }
  };

  const navigate = useNavigate();

  // Filter and sort projects
  const displayProjects = (() => {
    // First filter based on starred view
    const filtered = filter === 'starred' 
      ? projects.filter(project => favorites.has(project._id))
      : projects;
    
    // Then sort based on sortBy selection
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'last-visited') {
        // Sort by visitedAt descending (most recent first)
        const dateA = new Date(a.visitedAt || a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.visitedAt || b.updatedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      } else if (sortBy === 'name') {
        // Sort by name ascending (A-Z)
        return (a.name || '').localeCompare(b.name || '');
      }
      return 0;
    });
    
    return sorted;
  })();

  // Handle project click - navigate to chat with project loaded
  const handleProjectClick = (project) => {
    const tenantId = selectedTenant?.tenantId || project.tenantId;
    const projectId = project._id;
    
    if (!tenantId || !projectId) {
      console.error('Missing tenant or project ID', { tenantId, projectId });
      return;
    }
    
    // Track feature usage
    trackFeature('project_navigation');
    
    trackProject('open', { 
      projectId, 
      projectName: project.name,
      tenantId 
    });
    
    console.log('🎯 Navigating to project:', { tenantId, projectId, projectName: project.name });
    
    // Track project visit in recent projects
    const trackProjectVisit = async () => {
      try {
        const userEmail = user?.email || user?.sub || 'demo-user';
        const baseUrl = config.bffUrl || window.location.origin;
        
        console.log('🔵 [POST recent-projects] Projects page - tracking visit:', {
          url: `${baseUrl}/api/recent-projects`,
          userEmail,
          projectId: project._id
        });
        
        await getAuthorizedFetch()(`${baseUrl}/api/recent-projects`, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: getBffHeaders({
            'Content-Type': 'application/json',
            'x-user-id': userEmail,
          }),
          body: JSON.stringify(project),
        });
        
        console.log('✅ [POST recent-projects] Projects page - visit tracked successfully');
      } catch (error) {
        console.warn('❌ [POST recent-projects] Projects page - Failed to track project visit:', error);
      }
    };
    
    trackProjectVisit();
    
    // Clear ALL project-specific localStorage when navigating to different project
    // IMPORTANT: Clear BEFORE navigation to prevent race conditions with URL reconstruction
    // eslint-disable-next-line no-console
    console.log('🧹 Clearing all project-specific localStorage for project switch');
    localStorage.removeItem('mia_selectedResource');
    localStorage.removeItem('mia_selectedRevision');
    localStorage.removeItem('mia_activeTab');
    localStorage.removeItem('mia_fullProjectConfig');
    localStorage.removeItem('mia_selectedSession');
    
    // Set flag with target projectId to prevent URL reconstruction until project is loaded
    sessionStorage.setItem('mia_switching_to_project', projectId);
    
    // Clear all preview-session-* keys from localStorage
    // Note: This keeps render sessions alive so they can be reused when returning
    clearAllPreviewSessions();
    
    // Navigate to chat with tenant and project IDs in the URL (without service)
    navigate(`/chat/${tenantId}/${projectId}`);
  };

  return (
    <div className="projects-page">
      {/* Toast Notification */}
      {toast && (
        <div className="toast-container">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={hideToast}
          />
        </div>
      )}
      
      <div className="projects-header">
        <div className="projects-title-section">
          <h1 className="projects-title">
            {filter === 'starred' ? 'Starred Projects' : 'Projects'}
          </h1>
          <div className="projects-menu-container">
            <button className="projects-menu-btn" onClick={toggleProjectsMenu}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="3" cy="8" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                <circle cx="13" cy="8" r="1.5" fill="currentColor"/>
              </svg>
            </button>
            {isProjectsMenuOpen && (
              <div className="projects-menu-dropdown">
                <button
                  className="menu-item"
                  onClick={() => {
                    handleCreateProjectClick();
                    setIsProjectsMenuOpen(false);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="menu-icon">
                    <rect x="4" y="4" width="8" height="8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" rx="1"/>
                    <path d="M8 6V10M6 8H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>New Project</span>
                </button>
                <button
                  className="menu-item"
                  onClick={() => {
                    setIsCreateFolderModalOpen(true);
                    setIsProjectsMenuOpen(false);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="menu-icon">
                    <path d="M2 4.5C2 3.67157 2.67157 3 3.5 3H6.08579C6.35114 3 6.60557 3.10536 6.79289 3.29289L7.70711 4.20711C7.89443 4.39464 8.14886 4.5 8.41421 4.5H12.5C13.3284 4.5 14 5.17157 14 6V11.5C14 12.3284 13.3284 13 12.5 13H3.5C2.67157 13 2 12.3284 2 11.5V4.5Z" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  <span>New Folder</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="projects-controls">
          <div className="projects-search">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="search-icon">
              <path d="M7 12.5C10.0376 12.5 12.5 10.0376 12.5 7C12.5 3.96243 10.0376 1.5 7 1.5C3.96243 1.5 1.5 3.96243 1.5 7C1.5 10.0376 3.96243 12.5 7 12.5Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M14.5 14.5L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={handleSearch}
              className="projects-search-input"
            />
          </div>

          <div className="projects-filters">
            <select 
              className="filter-dropdown"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="last-visited">Last visited</option>
              <option value="name">Name</option>
            </select>

     

            <select 
              className="filter-dropdown"
              value={creatorFilter}
              onChange={(e) => setCreatorFilter(e.target.value)}
            >
              <option value="all">All creators</option>
              <option value="me">Me</option>
            </select>

            <button 
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('grid')}
              title="Grid view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="2" y="2" width="5" height="5" rx="1"/>
                <rect x="9" y="2" width="5" height="5" rx="1"/>
                <rect x="2" y="9" width="5" height="5" rx="1"/>
                <rect x="9" y="9" width="5" height="5" rx="1"/>
              </svg>
            </button>

            <button 
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('list')}
              title="List view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="2" y="3" width="12" height="2" rx="1"/>
                <rect x="2" y="7" width="12" height="2" rx="1"/>
                <rect x="2" y="11" width="12" height="2" rx="1"/>
              </svg>
            </button>

            <button 
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh projects"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={loading ? 'spinning' : ''}>
                <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C9.84871 2 11.4822 2.85714 12.5355 4.17157" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 1.5V4.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="projects-loading">
          <div className="spinner"></div>
          <p>Loading projects...</p>
        </div>
      )}

      {error && (
        <div className="projects-error">
          <p>❌ {error}</p>
          <button onClick={fetchProjects} className="retry-btn">Retry</button>
        </div>
      )}

      {!loading && !error && viewMode === 'grid' && (
        <div className="projects-grid">
          {/* Create new project card */}
          <div className="project-card create-card" onClick={handleCreateProjectClick}>
            <div className="create-card-content">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="create-icon">
                <rect x="12" y="12" width="24" height="24" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" rx="2"/>
                <path d="M24 18V30M18 24H30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p className="create-text">Create new project</p>
            </div>
          </div>

          {/* When searching, show all projects regardless of folder */}
          {searchQuery ? (
            displayProjects.map((project) => (
              <ProjectCard
                key={project._id}
                project={project}
                onClick={() => handleProjectClick(project)}
                isFavorite={favorites.has(project._id)}
                onFavoriteToggle={handleFavoriteToggle}
                hideFavorite={!showFavorites}
                folders={folders}
                onMoveToFolder={handleMoveToFolder}
                projectContextMenu={projectContextMenu}
                onContextMenu={setProjectContextMenu}
              />
            ))
          ) : (
            <>
              {/* Folder cards */}
              {folders.map(folder => {
                const isExpanded = expandedFolders.has(folder.folderId);
                const folderProjects = displayProjects.filter(p => p.folderId === folder.folderId);
                
                // In starred view, skip folders with no favorited projects
                if (filter === 'starred' && folderProjects.length === 0) {
                  return null;
                }
                
                return (
                  <React.Fragment key={folder.folderId}>
                    <div 
                      className="project-card folder-card"
                      onClick={() => {
                        const newExpanded = new Set(expandedFolders);
                        if (isExpanded) {
                          newExpanded.delete(folder.folderId);
                        } else {
                          newExpanded.add(folder.folderId);
                        }
                        setExpandedFolders(newExpanded);
                      }}
                      style={{ borderLeft: `4px solid ${folder.color}` }}
                    >
                      <div className="folder-card-content">
                        <div className="folder-header">
                          <svg width="32" height="32" viewBox="0 0 16 16" fill={folder.color} opacity="0.8">
                            <path d="M2 4v8a1 1 0 001 1h10a1 1 0 001-1V6H7L6 4H3a1 1 0 00-1 1z"/>
                          </svg>
                          <svg 
                            className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                            width="16" 
                            height="16" 
                            viewBox="0 0 16 16" 
                            fill="currentColor"
                          >
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          </svg>
                        </div>
                        <h3 className="folder-name">{folder.name}</h3>
                        {folder.description && (
                          <p className="folder-description">{folder.description}</p>
                        )}
                        <div className="folder-count">
                          {folderProjects.length} {folderProjects.length === 1 ? 'project' : 'projects'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Show folder projects when expanded */}
                    {isExpanded && folderProjects.map((project) => (
                      <ProjectCard
                        key={project._id}
                        project={project}
                        onClick={() => handleProjectClick(project)}
                        isFavorite={favorites.has(project._id)}
                        onFavoriteToggle={handleFavoriteToggle}
                        hideFavorite={!showFavorites}
                        folders={folders}
                        onMoveToFolder={handleMoveToFolder}
                        projectContextMenu={projectContextMenu}
                        onContextMenu={setProjectContextMenu}
                      />
                    ))}
                  </React.Fragment>
                );
              })}

              {/* Project cards without folders */}
              {displayProjects.filter(p => !p.folderId).map((project) => (
                <ProjectCard
                  key={project._id}
                  project={project}
                  onClick={() => handleProjectClick(project)}
                  isFavorite={favorites.has(project._id)}
                  onFavoriteToggle={handleFavoriteToggle}
                  hideFavorite={!showFavorites}
                  folders={folders}
                  onMoveToFolder={handleMoveToFolder}
                  projectContextMenu={projectContextMenu}
                  onContextMenu={setProjectContextMenu}
                />
              ))}
            </>
          )}
        </div>
      )}

      {!loading && !error && viewMode === 'list' && (
        <div className="projects-list">
          <div className="list-header">
            <div className="list-column name-column">NAME</div>
            <div className="list-column description-column">DESCRIPTION</div>
            <div className="list-column tech-column">TECH</div>
            <div className="list-column date-column">LAST OPENED</div>
            <div className="list-column owner-column">OWNER</div>
            <div className="list-column env-column">ENV</div>
            <div className="list-column actions-column"></div>
          </div>

          {/* When searching, show all projects regardless of folder */}
          {searchQuery ? (
            displayProjects.map((project) => {
              const technologies = project.info?.technologies || [];
              const description = project.description || project.info?.description || '—';
              const environments = project.environments || [];
              return (
              <div 
                key={project._id}
                className="list-row project-row"
                onClick={() => handleProjectClick(project)}
              >
                <div className="list-column name-column">
                  {project.thumbnail ? (
                    <img src={project.thumbnail} alt="" className="project-icon" />
                  ) : (
                    <div className="project-icon-placeholder">
                      {project.name?.substring(0, 2).toUpperCase() || 'PR'}
                    </div>
                  )}
                  <div className="project-info">
                    <span className="project-name">{project.name}</span>
                  </div>
                </div>
                <div className="list-column description-column" title={description}>
                  <span className="project-description">{description}</span>
                </div>
                <div className="list-column tech-column">
                  {technologies.length > 0 ? (
                    <div className="tech-badges-compact">
                      {technologies.slice(0, 2).map((tech, idx) => (
                        <span key={idx} className="tech-badge-compact">{tech}</span>
                      ))}
                      {technologies.length > 2 && (
                        <span className="tech-more">+{technologies.length - 2}</span>
                      )}
                    </div>
                  ) : '—'}
                </div>
                <div className="list-column date-column">
                  {formatDate(project.visitedAt || project.updatedAt || project.createdAt)}
                </div>
                <div className="list-column owner-column">{getOwnerName(project)}</div>
                <div className="list-column env-column">
                  {environments.length > 0 ? (
                    <span className="env-count">{environments.length} env{environments.length !== 1 ? 's' : ''}</span>
                  ) : '—'}
                </div>
                <div className="list-column actions-column">
                  <button
                    className="list-favorite-button"
                    onClick={(e) => handleFavoriteToggle(project._id, e)}
                    title={favorites.has(project._id) ? "Remove from favorites" : "Add to favorites"}
                  >
                    {favorites.has(project._id) ? '★' : '☆'}
                  </button>
                  {folders.length > 0 && (
                    <button
                      className="list-context-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectContextMenu(projectContextMenu === project._id ? null : project._id);
                      }}
                      title="More actions"
                    >
                      ⋯
                    </button>
                  )}
                </div>
              </div>
            );
            })
          ) : (
            <>
              {/* Folder rows */}
              {folders.map(folder => {
            const isExpanded = expandedFolders.has(folder.folderId);
            const folderProjects = displayProjects.filter(p => p.folderId === folder.folderId);
            
            // In starred view, skip folders with no favorited projects
            if (filter === 'starred' && folderProjects.length === 0) {
              return null;
            }
            
            return (
              <React.Fragment key={folder.folderId}>
                <div 
                  className="list-row folder-row"
                  onClick={() => {
                    if (renamingFolder === folder.folderId) return;
                    const newExpanded = new Set(expandedFolders);
                    if (isExpanded) {
                      newExpanded.delete(folder.folderId);
                    } else {
                      newExpanded.add(folder.folderId);
                    }
                    setExpandedFolders(newExpanded);
                  }}
                  style={{ borderLeft: `3px solid ${folder.color}` }}
                >
                  <div className="list-column name-column">
                    <svg 
                      className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                      width="16" 
                      height="16" 
                      viewBox="0 0 16 16" 
                      fill="currentColor"
                    >
                      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill={folder.color} opacity="0.8">
                      <path d="M2 4v8a1 1 0 001 1h10a1 1 0 001-1V6H7L6 4H3a1 1 0 00-1 1z"/>
                    </svg>
                    {renamingFolder === folder.folderId ? (
                      <input
                        type="text"
                        className="folder-rename-input"
                        value={renameFolderValue}
                        onChange={(e) => setRenameFolderValue(e.target.value)}
                        onBlur={() => handleRenameFolder(folder.folderId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleRenameFolder(folder.folderId);
                          } else if (e.key === 'Escape') {
                            handleCancelRenameFolder();
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      <>
                        <span className="folder-name">{folder.name}</span>
                        <span className="project-count">{folderProjects.length} {folderProjects.length === 1 ? 'project' : 'projects'}</span>
                      </>
                    )}
                  </div>
                  <div className="list-column description-column"></div>
                  <div className="list-column tech-column"></div>
                  <div className="list-column date-column">
                    {new Date(folder.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div className="list-column owner-column">{folder.createdBy || user?.name || 'Unknown'}</div>
                  <div className="list-column env-column"></div>
                  <div className="list-column actions-column">
                    <button
                      className="list-action-button"
                      onClick={(e) => handleStartRenameFolder(folder, e)}
                      title="Rename folder"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                      </svg>
                    </button>
                    <button
                      className="list-action-button delete-button"
                      onClick={(e) => handleConfirmDeleteFolder(folder.folderId, e)}
                      title="Delete folder"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Show folder projects when expanded */}
                {isExpanded && folderProjects.map((project) => {
                  const technologies = project.info?.technologies || [];
                  const description = project.description || project.info?.description || '—';
                  const environments = project.environments || [];
                  return (
                  <div 
                    key={project._id}
                    className="list-row project-row nested"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="list-column name-column">
                      {project.thumbnail ? (
                        <img src={project.thumbnail} alt="" className="project-icon" />
                      ) : (
                        <div className="project-icon-placeholder">
                          {project.name?.substring(0, 2).toUpperCase() || 'PR'}
                        </div>
                      )}
                      <div className="project-info">
                        <span className="project-name">{project.name}</span>
                      </div>
                    </div>
                    <div className="list-column description-column" title={description}>
                      <span className="truncate-text">{description}</span>
                    </div>
                    <div className="list-column tech-column">
                      {technologies.length > 0 ? (
                        <div className="tech-badges-compact">
                          {technologies.slice(0, 2).map((tech, idx) => (
                            <span key={idx} className="tech-badge-compact">{tech}</span>
                          ))}
                          {technologies.length > 2 && (
                            <span className="tech-more">+{technologies.length - 2}</span>
                          )}
                        </div>
                      ) : '—'}
                    </div>
                    <div className="list-column date-column">
                      {formatDate(project.visitedAt || project.updatedAt || project.createdAt)}
                    </div>
                    <div className="list-column owner-column">{getOwnerName(project)}</div>
                    <div className="list-column env-column">
                      {environments.length > 0 ? (
                        <span className="env-count">{environments.length} env{environments.length !== 1 ? 's' : ''}</span>
                      ) : '—'}
                    </div>
                    <div className="list-column actions-column">
                      <button
                        className="list-favorite-button"
                        onClick={(e) => handleFavoriteToggle(project._id, e)}
                        title={favorites.has(project._id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        {favorites.has(project._id) ? '★' : '☆'}
                      </button>
                      {folders.length > 0 && (
                        <button
                          className="list-context-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProjectContextMenu(projectContextMenu === project._id ? null : project._id);
                          }}
                          title="More actions"
                        >
                          ⋯
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </React.Fragment>
            );
          })}

              {/* Project rows without folders */}
              {displayProjects.filter(p => !p.folderId).map((project) => {
                const technologies = project.info?.technologies || [];
                const description = project.description || project.info?.description || '—';
                const environments = project.environments || [];
                return (
                <div 
                  key={project._id}
                  className="list-row project-row"
                  onClick={() => handleProjectClick(project)}
                >
              <div className="list-column name-column">
                {project.thumbnail ? (
                  <img src={project.thumbnail} alt="" className="project-icon" />
                ) : (
                  <div className="project-icon-placeholder">
                    {project.name?.substring(0, 2).toUpperCase() || 'PR'}
                  </div>
                )}
                <div className="project-info">
                  <span className="project-name">{project.name}</span>
                </div>
              </div>
              <div className="list-column description-column" title={description}>
                <span className="truncate-text">{description}</span>
              </div>
              <div className="list-column tech-column">
                {technologies.length > 0 ? (
                  <div className="tech-badges-compact">
                    {technologies.slice(0, 2).map((tech, idx) => (
                      <span key={idx} className="tech-badge-compact">{tech}</span>
                    ))}
                    {technologies.length > 2 && (
                      <span className="tech-more">+{technologies.length - 2}</span>
                    )}
                  </div>
                ) : '—'}
              </div>
              <div className="list-column date-column">
                {formatDate(project.visitedAt || project.updatedAt || project.createdAt)}
              </div>
              <div className="list-column owner-column">{getOwnerName(project)}</div>
              <div className="list-column env-column">
                {environments.length > 0 ? (
                  <span className="env-count">{environments.length} env{environments.length !== 1 ? 's' : ''}</span>
                ) : '—'}
              </div>
              <div className="list-column actions-column">
                <button
                  className="list-favorite-button"
                  onClick={(e) => handleFavoriteToggle(project._id, e)}
                  title={favorites.has(project._id) ? "Remove from favorites" : "Add to favorites"}
                >
                  {favorites.has(project._id) ? '★' : '☆'}
                </button>
                {folders.length > 0 && (
                  <button
                    className="list-context-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProjectContextMenu(projectContextMenu === project._id ? null : project._id);
                    }}
                    title="More actions"
                  >
                    ⋯
                  </button>
                )}
                </div>
              </div>
              );
              })}
            </>
          )}
        </div>
      )}

      {/* Create project modal */}
      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => {
          setIsCreateProjectModalOpen(false);
          setInitialProjectPrompt('');
        }}
        onCreateProject={handleCreateProject}
        selectedTenant={selectedTenant}
        initialPrompt={initialProjectPrompt}
      />

      {/* Create folder modal */}
      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => setIsCreateFolderModalOpen(false)}
        onCreateFolder={handleCreateFolder}
        tenantId={selectedTenant?.tenantId || ''}
      />

      {/* Delete folder confirmation modal */}
      {deletingFolder && createPortal(
        <div className="modal-overlay" onClick={handleCancelDeleteFolder}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Folder</h2>
            <p>Are you sure you want to delete this folder?</p>
            <p style={{ fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
              Note: Projects in this folder will be moved to the root level. Folders with subfolders cannot be deleted.
            </p>
            <div className="modal-buttons">
              <button onClick={handleCancelDeleteFolder} className="cancel-button">
                Cancel
              </button>
              <button onClick={() => handleDeleteFolder(deletingFolder)} className="delete-button">
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Projects;
